import React, { useState } from 'react';
import {
  Page,
  Card,
  FormLayout,
  Select,
  DatePicker,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Checkbox,
  Layout,
  ProgressBar,
  Text,
} from '@shopify/polaris';
import { useQuery, useMutation } from 'react-query';
import api from '../services/api';
import { useConfig } from '../contexts/ConfigContext';

interface ExportFilters {
  status: string;
  financial_status: string;
  fulfillment_status: string;
  created_at_min?: string;
  created_at_max?: string;
}

const ExportOrders: React.FC = () => {
  const { config } = useConfig();
  const [filters, setFilters] = useState<ExportFilters>({
    status: 'any',
    financial_status: 'paid',
    fulfillment_status: 'any',
  });
  const [format, setFormat] = useState<string>(config?.defaultFormat || 'xml');
  const [tallyVersion, setTallyVersion] = useState<string>(config?.tallyVersion || 'prime');
  const [includeShipping, setIncludeShipping] = useState(true);
  const [includeTax, setIncludeTax] = useState(true);
  const [includeDiscounts, setIncludeDiscounts] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  const [selectedDates, setSelectedDates] = useState({ start: new Date(), end: new Date() });

  const exportMutation = useMutation(
    () => api.orders.exportOrders({
      format,
      tallyVersion,
      includeShipping,
      includeTax,
      includeDiscounts,
      filters: {
        ...filters,
        created_at_min: selectedDates.start.toISOString(),
        created_at_max: selectedDates.end.toISOString(),
      },
    }),
    {
      onSuccess: (response) => {
        if (response.data.success && response.data.data.downloadUrl) {
          window.open(response.data.data.downloadUrl, '_blank');
        }
      },
    }
  );

  const previewQuery = useQuery(
    ['preview', filters],
    () => api.orders.previewExport({ filters }),
    { enabled: false }
  );

  const handleExport = () => {
    exportMutation.mutate();
  };

  const handlePreview = () => {
    previewQuery.refetch();
  };

  return (
    <Page
      title="Export Orders"
      subtitle="Export your Shopify orders to Tally compatible format"
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Export Settings</Text>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    label="Export Format"
                    options={[
                      { label: 'XML (Recommended)', value: 'xml' },
                      { label: 'Excel', value: 'excel' },
                      { label: 'Both', value: 'both' },
                    ]}
                    value={format}
                    onChange={setFormat}
                  />
                  <Select
                    label="Tally Version"
                    options={[
                      { label: 'TallyPrime', value: 'prime' },
                      { label: 'Tally.ERP 9', value: 'erp9' },
                    ]}
                    value={tallyVersion}
                    onChange={setTallyVersion}
                  />
                </FormLayout.Group>
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Order Filters</Text>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    label="Order Status"
                    options={[
                      { label: 'Any', value: 'any' },
                      { label: 'Open', value: 'open' },
                      { label: 'Closed', value: 'closed' },
                      { label: 'Cancelled', value: 'cancelled' },
                    ]}
                    value={filters.status}
                    onChange={(value) => setFilters({ ...filters, status: value })}
                  />
                  <Select
                    label="Payment Status"
                    options={[
                      { label: 'Paid', value: 'paid' },
                      { label: 'Pending', value: 'pending' },
                      { label: 'Partially Paid', value: 'partially_paid' },
                      { label: 'Refunded', value: 'refunded' },
                      { label: 'Any', value: 'any' },
                    ]}
                    value={filters.financial_status}
                    onChange={(value) => setFilters({ ...filters, financial_status: value })}
                  />
                  <Select
                    label="Fulfillment Status"
                    options={[
                      { label: 'Any', value: 'any' },
                      { label: 'Fulfilled', value: 'fulfilled' },
                      { label: 'Unfulfilled', value: 'unfulfilled' },
                      { label: 'Partial', value: 'partial' },
                    ]}
                    value={filters.fulfillment_status}
                    onChange={(value) => setFilters({ ...filters, fulfillment_status: value })}
                  />
                </FormLayout.Group>

                <DatePicker
                  month={selectedMonth.month}
                  year={selectedMonth.year}
                  onChange={setSelectedDates}
                  onMonthChange={(month, year) => setSelectedMonth({ month, year })}
                  selected={selectedDates}
                  allowRange
                />
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Include Options</Text>
              <BlockStack gap="200">
                <Checkbox
                  label="Include shipping charges"
                  checked={includeShipping}
                  onChange={setIncludeShipping}
                />
                <Checkbox
                  label="Include taxes"
                  checked={includeTax}
                  onChange={setIncludeTax}
                />
                <Checkbox
                  label="Include discounts"
                  checked={includeDiscounts}
                  onChange={setIncludeDiscounts}
                />
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {exportMutation.isLoading && (
          <Layout.Section>
            <Card>
              <BlockStack gap="200">
                <ProgressBar progress={75} />
                <Text variant="bodyMd" as="p" alignment="center">
                  Exporting orders... This may take a moment.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {exportMutation.isSuccess && (
          <Layout.Section>
            <Banner
              title="Export Successful"
              tone="success"
              onDismiss={() => exportMutation.reset()}
            >
              <p>
                Successfully exported {exportMutation.data?.data?.data?.orderCount || 0} orders.
                Your download should start automatically.
              </p>
            </Banner>
          </Layout.Section>
        )}

        {exportMutation.isError && (
          <Layout.Section>
            <Banner
              title="Export Failed"
              tone="critical"
              onDismiss={() => exportMutation.reset()}
            >
              <p>Failed to export orders. Please try again.</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <InlineStack align="end" gap="200">
            <Button onClick={handlePreview} disabled={exportMutation.isLoading}>
              Preview
            </Button>
            <Button variant="primary" onClick={handleExport} loading={exportMutation.isLoading}>
              Export Orders
            </Button>
          </InlineStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default ExportOrders;
